import React from 'react';
import { Button, Card, Flex, Grid, Space, Typography, theme } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { Link, type LinkProps } from '@tanstack/react-router';

const { Title } = Typography;
const { useBreakpoint } = Grid;

interface DetailPageHeaderProps {
  /** Icon shown in the 48px tile next to the title. */
  icon: React.ReactNode;
  title: React.ReactNode;
  /** Tags and secondary meta text under the title. */
  meta?: React.ReactNode;
  /** Optional second row under the tags, e.g. contact details with icons. */
  details?: React.ReactNode;
  /** Page actions, rendered before the back button. */
  actions?: React.ReactNode;
  /** Route the back button links to; ignored when `onBack` is set. */
  backTo?: string;
  /** Search params for the `backTo` link, e.g. to restore list filters. */
  backSearch?: LinkProps['search'];
  /** Custom back handler (e.g. history back) used instead of the `backTo` link. */
  onBack?: () => void;
  backLabel: string;
}

/**
 * Header card used by the manage detail pages (resources, services, leads,
 * threads): icon tile, title with a tag row, then actions and a back button.
 */
export function DetailPageHeader({ icon, title, meta, details, actions, backTo, backSearch, onBack, backLabel }: DetailPageHeaderProps) {
  const { token } = theme.useToken();
  // On phones the 48px icon tile would squeeze the title, and the card padding shrinks.
  const isPhone = useBreakpoint().sm === false;

  return (
    <Card
      styles={{ body: { padding: isPhone ? token.padding : token.paddingLG } }}
      style={{
        marginBottom: token.margin,
      }}
    >
      <Flex justify="space-between" align="center" wrap="wrap" gap="middle">
        {/* A real flex basis makes wide action sets wrap below the title instead of squeezing it. */}
        <Flex align="center" gap="middle" style={{ flex: '1 1 360px', minWidth: 0 }}>
          {!isPhone && (
            <Flex
              align="center"
              justify="center"
              style={{
                width: 48,
                height: 48,
                flexShrink: 0,
                borderRadius: token.borderRadius,
                backgroundColor: token.colorFillTertiary,
                fontSize: 24,
                color: token.colorPrimary
              }}
            >
              {icon}
            </Flex>
          )}
          <Flex vertical gap={4} style={{ minWidth: 0 }}>
            <Title level={isPhone ? 4 : 3} style={{ margin: 0, fontWeight: 600 }}>
              {title}
            </Title>
            {meta && (
              <Flex align="center" gap="small" wrap="wrap">
                {meta}
              </Flex>
            )}
            {details && (
              <Flex align="center" wrap="wrap" gap={`${token.marginXS}px ${token.marginLG}px`} style={{ marginTop: token.marginXXS }}>
                {details}
              </Flex>
            )}
          </Flex>
        </Flex>
        <Space wrap>
          {actions}
          {onBack ? (
            <Button type="default" icon={<ArrowLeftOutlined />} onClick={onBack}>
              {backLabel}
            </Button>
          ) : backTo ? (
            <Link to={backTo} search={backSearch}>
              <Button type="default" icon={<ArrowLeftOutlined />}>
                {backLabel}
              </Button>
            </Link>
          ) : null}
        </Space>
      </Flex>
    </Card>
  );
}

export default DetailPageHeader;
